import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { CheckCircle, Warning } from "@phosphor-icons/react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [status, setStatus] = useState("polling");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("failed"); return;
        }
      } catch { /* transient */ }
      attemptsRef.current += 1;
      if (attemptsRef.current > 30) { setStatus("timeout"); return; }
    };

    poll();
    const iv = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [sessionId]);

  // Stop polling once terminal
  useEffect(() => {
    // no-op; the effect above returns cleanup that clears the interval on unmount.
    // Terminal states (paid/failed/timeout/error) unmount naturally on user nav.
  }, [status]);

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center p-6 grain">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-white rounded-3xl border border-black/5 p-10 text-center">
        {status === "paid" && (
          <>
            <CheckCircle size={64} weight="duotone" className="mx-auto text-ayurveda mb-4" />
            <h1 className="font-heritage text-4xl text-ayurveda mb-3">Payment received</h1>
            <p className="text-charcoal/70 mb-6">Thank you. Your appointment is confirmed. A WhatsApp reminder has been queued.</p>
            <Button onClick={() => nav("/app/appointments")} className="bg-ayurveda text-bone" data-testid="pay-success-back">Go to my appointments</Button>
          </>
        )}
        {status === "polling" && (
          <>
            <div className="w-16 h-16 rounded-full border-4 border-ayurveda border-t-transparent animate-spin mx-auto mb-4" />
            <h1 className="font-heritage text-3xl text-ayurveda mb-2">Confirming payment…</h1>
            <p className="text-charcoal/60 text-sm">This usually takes a few seconds.</p>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <Warning size={56} weight="duotone" className="mx-auto text-terracotta mb-4" />
            <h1 className="font-heritage text-3xl text-ayurveda mb-2">Payment not confirmed</h1>
            <p className="text-charcoal/60 text-sm mb-4">Please try again or contact support.</p>
            <Button onClick={() => nav("/app/appointments")} variant="outline">Back</Button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-bone flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl border border-black/5 p-10 text-center">
        <Warning size={56} weight="duotone" className="mx-auto text-terracotta mb-4" />
        <h1 className="font-heritage text-3xl text-ayurveda mb-2">Payment cancelled</h1>
        <p className="text-charcoal/60 text-sm mb-6">You can retry the payment any time from your appointments.</p>
        <Button onClick={() => nav("/app/appointments")} className="bg-ayurveda text-bone" data-testid="pay-cancel-back">Back to appointments</Button>
      </div>
    </div>
  );
}
